import { useEffect, useMemo, useRef, useState } from "react";

const WSS_ENDPOINT = process.env.REACT_APP_WSS_ENDPOINT;

function canUseRealtime({ token, enabled }) {
  return Boolean(
    enabled && token && WSS_ENDPOINT && typeof window !== "undefined"
  );
}

export function useRealtimeResults({ token, enabled = true } = {}) {
  const [events, setEvents] = useState([]);
  const [lastEvent, setLastEvent] = useState(null);
  const [connectionState, setConnectionState] = useState("idle");
  const wsRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const shouldReconnectRef = useRef(false);
  const backoffRef = useRef(1000);

  useEffect(() => {
    if (!canUseRealtime({ token, enabled })) {
      const reason = !WSS_ENDPOINT
        ? "missing-endpoint"
        : !token
        ? "no-token"
        : "disabled";
      console.log("[useRealtimeResults] ⏸️ Realtime disabled", {
        reason,
        WSS_ENDPOINT: !!WSS_ENDPOINT,
        hasToken: !!token,
        enabled,
      });
      setConnectionState(WSS_ENDPOINT ? "disabled" : "missing-endpoint");
      return undefined;
    }

    shouldReconnectRef.current = true;

    const connect = () => {
      try {
        const url = new URL(WSS_ENDPOINT);
        url.searchParams.set("token", token);

        console.log("[useRealtimeResults] 🔌 Connecting to WebSocket", {
          url: url.toString().replace(token, "***"),
        });
        setConnectionState("connecting");
        const ws = new WebSocket(url.toString());
        wsRef.current = ws;

        ws.onopen = () => {
          console.log("[useRealtimeResults] ✅ WebSocket connected");
          setConnectionState("open");
          backoffRef.current = 1000;
        };

        ws.onmessage = (event) => {
          try {
            const payload = JSON.parse(event.data);
            console.log("[useRealtimeResults] 📩 Message received", {
              eventType: payload?.eventType,
              resultId: payload?.resultId,
              userId: payload?.userId,
            });
            setEvents((prev) => {
              const next = [...prev, payload];
              return next.slice(-100);
            });
            setLastEvent(payload);
          } catch (err) {
            console.warn(
              "[useRealtimeResults] ⚠️ Invalid realtime payload",
              err?.message,
              event.data?.slice?.(0, 100)
            );
          }
        };

        ws.onerror = (err) => {
          console.error("[useRealtimeResults] ❌ WebSocket error", err);
          setConnectionState("error");
          ws.close();
        };

        ws.onclose = (closeEvent) => {
          console.log("[useRealtimeResults] 🔒 WebSocket closed", {
            code: closeEvent.code,
            reason: closeEvent.reason,
            wasClean: closeEvent.wasClean,
          });
          setConnectionState("closed");
          if (shouldReconnectRef.current) {
            const delay = Math.min(backoffRef.current, 10000);
            console.log("[useRealtimeResults] 🔄 Reconnecting in", delay, "ms");
            reconnectTimerRef.current = window.setTimeout(() => {
              backoffRef.current = Math.min(backoffRef.current * 1.5, 10000);
              connect();
            }, delay);
          }
        };
      } catch (err) {
        console.error(
          "[useRealtimeResults] ❌ Failed to initialize websocket",
          err?.message
        );
        setConnectionState("error");
        reconnectTimerRef.current = window.setTimeout(() => {
          backoffRef.current = Math.min(backoffRef.current * 1.5, 10000);
          connect();
        }, backoffRef.current);
      }
    };

    connect();

    return () => {
      console.log("[useRealtimeResults] 🧹 Cleanup: closing WebSocket");
      shouldReconnectRef.current = false;
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [token, enabled]);

  const jobs = useMemo(() => {
    const map = new Map();
    events.forEach((evt) => {
      if (!evt?.resultId) return;
      const existing = map.get(evt.resultId) || { history: [] };
      map.set(evt.resultId, {
        lastEvent: evt,
        history: [...existing.history, evt].slice(-5),
      });
    });
    return map;
  }, [events]);

  return {
    events,
    jobs,
    lastEvent,
    connectionState,
    isEnabled: canUseRealtime({ token, enabled }),
  };
}

export default useRealtimeResults;
